import { combineEpics } from 'redux-observable';
import { defer, EMPTY, MonoTypeOperatorFunction, Observable, of } from 'rxjs';
import { concatAll, filter, map, mergeMap, mergeMapTo } from 'rxjs/operators';

import {
	elearningComponentUpdated,
	ELearningKind,
	initTerminal,
	showELearningFinishDialog,
} from 'src/actions/eLearning';
import { initExcerciseForm } from 'src/actions/exerciseForm';
import { initQuestionnaire } from 'src/actions/questionnaire';

import ELearningScenarioFactory, {
	IFetchELearningProgressParam,
} from 'src/scenario/ELearningScenarioFactory';

import { EpicType, ofAction } from './common';

/**
 * SCORM APIのflush処理
 */
export const flushScorm = async () => window.API && window.API.flush();

type A = Parameters<
	Parameters<typeof ELearningScenarioFactory.createFetchELearningProgress>[0]
>[0];

/**
 * シナリオを叩いて進捗を取得
 * @param token sessionトークン: storeから取得
 * @param params 進捗取得用パラメータ
 */
export const fetchProgress = (
	token: string,
	params: IFetchELearningProgressParam
) =>
	new Observable<A>(observer => {
		ELearningScenarioFactory.createFetchELearningProgress(action => {
			observer.next(action);
			return action;
		}, token)
			.run(params)
			.catch((e: any) => {
				// tslint:disable-next-line:no-console
				console.error('fail to fetch progress', e);
			})
			.finally(() => observer.complete());
	});

/**
 * 終端ページ用ダイアログを表示
 */
export const showTerminalDialog = async () => {
	return showELearningFinishDialog({ isOpen: true });
};

/**
 * SCORM API のflushを行い、flushしたときだけ次にActionを流すOperator
 */
export const withFlushingScorm = <T>(): MonoTypeOperatorFunction<
	T
> => action$ =>
	action$.pipe(
		// SCORM締め
		mergeMap(action =>
			// scorm apiのflush
			defer(flushScorm).pipe(
				// actionと合わせて返却
				map(flushed => [action, flushed] as const)
			)
		),
		// SCORMのflushがあったときだけ
		filter(([, flushed]) => flushed),
		map(([action]) => action)
	);

/**
 * 終端ページ初期化処理
 * @param action$
 * @param state$
 *
 * @deprecated 不具合対応で未使用に
 */
export const initTerminalEpic: EpicType = (action$, state$) =>
	action$.pipe(
		ofAction(initTerminal),
		mergeMap(action =>
			of(
				// SCORMをflush -> 必要なら進捗再取得
				of(action).pipe(
					withFlushingScorm(),
					mergeMapTo(
						fetchProgress(
							state$.value.session.token,
							action.payload
						)
					)
				),
				// 最後には必ず終端ダイアログ
				defer(async () => await showTerminalDialog())
			).pipe(concatAll())
		)
	);

/**
 * MergeMap初期化処理
 * @param action$
 * @param state$
 *
 * @deprecated 不具合対応で未使用に
 */
export const initAdditionalCareerItemEpic: EpicType = (action$, state$) =>
	action$.pipe(
		ofAction(initQuestionnaire, initExcerciseForm),
		withFlushingScorm(),
		// 進捗取得 -> ダイアログ表示
		mergeMap(action =>
			fetchProgress(state$.value.session.token, action.payload)
		)
	);

/**
 * eラーニング目次遷移時処理
 * @param action$
 * @param state$
 *
 * @remarks
 * 現実装ではELearningComponentのcomponentDidUpdateのたびに呼ばれる
 */
export const transitionIndicesEpic: EpicType = (action$, state$) =>
	action$.pipe(
		ofAction(elearningComponentUpdated),
		mergeMap(action =>
			// for concatAll
			of(
				// SCORMをflush -> 必要なら進捗再取得
				of(action).pipe(
					// SCORMコンテンツに限ってはページの頭で正しくハンドリングする必要があるため除外
					filter(
						({ payload: { next } }) =>
							!next || next.kind !== ELearningKind.SCORM
					),
					// その他の場合にflush()
					withFlushingScorm(),
					// flush対象が存在した場合に進捗率更新
					mergeMapTo(
						fetchProgress(
							state$.value.session.token,
							action.payload.progress
						)
					)
				),
				// 終端ページのときのみ、SCORMがどうであろうと最後に終端ダイアログ
				// 終端ページのままupdateが来たときには重複して発行しないよう条件付け
				(!action.payload.previous ||
					action.payload.previous.kind !== ELearningKind.TERMINAL) &&
					action.payload.next &&
					action.payload.next.kind === ELearningKind.TERMINAL
					? defer(async () => await showTerminalDialog())
					: EMPTY
			).pipe(concatAll())
		)
	);

/**
 * ELearning関連Epic
 * @param action$ ActionsObservable
 */
export const eLearningEpic: EpicType = combineEpics(transitionIndicesEpic);



// WEBPACK FOOTER //
// ./src/epics/eLearning.ts